import * as THREE from 'three';
import { create } from 'zustand';

export type ViewPlaneVisualizationMode = 'dots' | 'heatmap' | 'both';
export type ViewPlaneBlockingMode = 'plane' | 'source';

export interface ViewPlaneCategoryStat {
	category: string;
	count: number;
	percentage: number;
	color: string;
	averageDistance: number;
}

export interface ViewPlaneInspectorCategorySummary {
	category: string;
	count: number;
	blockedCount: number;
	openCount: number;
	avgHitDistance: number;
	color: string;
}

export interface ViewPlaneInspectorResult {
	totalRays: number;
	blockedCount: number;
	openCount: number;
	categories: ViewPlaneInspectorCategorySummary[];
}

export interface SelectedViewPlaneStats {
	windowId: string;
	totalIntersections: number;
	planeDistance: number;
	width: number;
	height: number;
	averageDistance: number;
	categories: ViewPlaneCategoryStat[];
}

interface ThreeSceneStore {
	isHoleCreationMode: boolean;
	setIsHoleCreationMode: (value: boolean) => void;
	isHoleDeletionMode: boolean;
	setIsHoleDeletionMode: (value: boolean) => void;
	isFacadeViewAnalysisMode: boolean;
	setIsFacadeViewAnalysisMode: (value: boolean) => void;
	areEventsPaused: boolean;
	setAreEventsPaused: (value: boolean) => void;
	currentTarget: THREE.Object3D | null;
	setCurrentTarget: (target: THREE.Object3D | null) => void;
	showStats: boolean;
	setShowStats: (value: boolean) => void;
	selectedViewPlaneStats: SelectedViewPlaneStats | null;
	setSelectedViewPlaneStats: (stats: SelectedViewPlaneStats | null) => void;
	viewPlaneVisualizationMode: ViewPlaneVisualizationMode;
	setViewPlaneVisualizationMode: (mode: ViewPlaneVisualizationMode) => void;
	viewPlaneBlockingMode: ViewPlaneBlockingMode;
	setViewPlaneBlockingMode: (mode: ViewPlaneBlockingMode) => void;
	viewPlaneCategoryFilter: string | null;
	setViewPlaneCategoryFilter: (category: string | null) => void;
	viewPlaneInspector: ViewPlaneInspectorResult | null;
	setViewPlaneInspector: (result: ViewPlaneInspectorResult | null) => void;
	viewPlaneSampleScale: number;
	setViewPlaneSampleScale: (scale: number) => void;
	hasExtrudedWalls: boolean;
	setHasExtrudedWalls: (value: boolean) => void;
	resetAllModes: () => void; // Helper to reset all modes
}

export const useThreeSceneStore = create<ThreeSceneStore>((set) => {
	return {
		isHoleCreationMode: false,
		setIsHoleCreationMode: (value) =>
			set((state) => {
				return { ...state, isHoleCreationMode: value, isHoleDeletionMode: false };
			}),
		isHoleDeletionMode: false,
		setIsHoleDeletionMode: (value) =>
			set((state) => {
				return { ...state, isHoleDeletionMode: value, isHoleCreationMode: false };
			}),
		isFacadeViewAnalysisMode: false,
		setIsFacadeViewAnalysisMode: (value) => set(() => ({ isFacadeViewAnalysisMode: value })),
		areEventsPaused: false,
		setAreEventsPaused: (value) => set(() => ({ areEventsPaused: value })),
		currentTarget: null,
		setCurrentTarget: (target) => set(() => ({ currentTarget: target })),
		showStats: false,
		setShowStats: (value) => set(() => ({ showStats: value })),
		selectedViewPlaneStats: null,
		setSelectedViewPlaneStats: (stats) => set(() => ({ selectedViewPlaneStats: stats })),
		viewPlaneVisualizationMode: 'both',
		setViewPlaneVisualizationMode: (mode) => set(() => ({ viewPlaneVisualizationMode: mode })),
		viewPlaneBlockingMode: 'plane',
		setViewPlaneBlockingMode: (mode) => set(() => ({ viewPlaneBlockingMode: mode })),
		viewPlaneCategoryFilter: null,
		setViewPlaneCategoryFilter: (category) => set(() => ({ viewPlaneCategoryFilter: category })),
		viewPlaneInspector: null,
		setViewPlaneInspector: (result) => set(() => ({ viewPlaneInspector: result })),
		viewPlaneSampleScale: 1,
		setViewPlaneSampleScale: (scale) => set(() => ({ viewPlaneSampleScale: Math.min(2, Math.max(0.5, scale)) })),
		hasExtrudedWalls: false,
		setHasExtrudedWalls: (value) => set(() => ({ hasExtrudedWalls: value })),
		resetAllModes: () => set(() => ({
			isHoleCreationMode: false,
			isHoleDeletionMode: false
		})),
	};
});
